import { Mobile } from '@bimser/common';
import { BCExtremeGridEntities, findMessage } from '@bimser/components';
import { IDictionary, MLHelper, createGuid, isNullOrUndefined } from '@bimser/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import isNaN from 'lodash/isNaN';
import {
	convertFieldsForMobile,
	convertValuesAsString,
	filteringStaticForMobile,
	handleDataForMobile,
	handleRowsForMobile,
	onBeforeToolbarItemClick,
	onColumnButtonClick,
	onPageIndexChange,
	onPageSizeChange,
	onRowRemoved,
	onRowRemoving,
	onRowUpdated,
	onRowUpdating,
	onSearchingChange,
	onSelectionChange,
	onSortingChange
} from '..';
import DataGrid from '../..';
import { IControlProperties, IDataGridColumnCollection } from '../../../../../../entities';
import { IDataLoadEventArgs, IRowUpdatingEventArgs } from '../../../../entities/EventArgs';
import {
	IControlDataGridColumn,
	IDataGridCommitEvent,
	IExecuteActionButton,
	Order
} from '../../entities';

dayjs.extend(localizedFormat);
dayjs.extend(localeData);

export let primaryKeyControl = false;

export function subscribeMobilEvents(this: DataGrid) {
	this.subscribe('trigger:OnSelectionChange', (data: string) => {
		const selectedRows: Array<any> = JSON.parse(data);
		const uniqueField = this.getUniqueDataField();
		const selectedRowMobile = removeDuplicateObjects(selectedRows, uniqueField);
		const selectedRowIdsMobile: any[] = selectedRowMobile.map(
			selectedRow => selectedRow[uniqueField]
		);

		onSelectionChange.call(this, selectedRowMobile, selectedRowIdsMobile);
	});

	this.subscribe('trigger:OnClickActionButton', (value: string) => {
		const args: { value: string; columnName: string } = JSON.parse(value);
		const rows = getRowsWithCells(this.rows, true);

		if (args.value == 'null') {
			args.value = null;
		}

		const selectedRow = rows.find(
			row => row[this.getUniqueDataField()]?.toString() === args.value?.toString()
		);
		const selectedColumn = this.columns.find(column => column.name === args.columnName);

		onColumnButtonClick.call(this, { row: selectedRow }, selectedColumn);
	});

	this.subscribe('trigger:OnToolbarItemClick', (item: string) => {
		const toolbarItem: IExecuteActionButton<any> = JSON.parse(item);

		if (toolbarItem?.args?.showOn) {
			toolbarItem.args.showOn = 'panel';
		}

		onBeforeToolbarItemClick.call(this, toolbarItem);
	});

	this.subscribe('trigger:OnStartingRowEdit', (key: string) => {
		const primaryColumn = this.columns.find(item => item.isPrimaryKey);

		const editRowKey = this.rows.find(item =>
			rowEditingPrimaryControl(item.data[primaryColumn.name], key, primaryColumn)
		).index;

		this.__dataGridRef?.instance?.().editRow(editRowKey);

		const uniqueDataField = this.getUniqueDataField();
		const rows = getRowsWithCells(this.rows);

		const rowData = rows.find(row =>
			rowEditingPrimaryControl(row[uniqueDataField], key, primaryColumn)
		);

		Object.keys(rowData).forEach(rowDataKey => {
			const type = this.columns.find(column => column.name === rowDataKey)?.editType;
			if (type) {
				const value = convertValuesAsString(rowDataKey, rowData[rowDataKey], type);
				this.__editingColumnsValues = handleDataForMobile(
					this.__editingColumnsValues,
					value,
					type,
					this.columns,
					this.__columns
				);
			}
		});
		primaryKeyControl = true;
		const mobileControls = convertFieldsForMobile(
			this.columns as any,
			this.__editingColumnsValues,
			this.__columns,
			this.name,
			true
		);
		Mobile.MobileView.setDataGridControlsProperties(mobileControls, this.name);
	});

	this.subscribe('trigger:OnCancelRowEdit', () => {
		this.__dataGridRef?.instance?.().closeEditCell();
		primaryKeyControl = false;
		this.__editingColumnsValues = [];
		this.reload(true);
	});

	this.subscribe('trigger:OnInitNewRow', async () => {
		const item = await this.onInitNewRow();
		let initValues;
		let fieldValues = undefined;
		if (item) {
			initValues = Object.entries(item).map(([name, value]) => ({
				name,
				value: typeof value == 'object' ? (value as any).value || '' : value || '',
				rowValue: typeof value == 'object' ? (value as any).value || '' : value || ''
			}));

			fieldValues = this.columns.map(field => {
				const matchingItem = initValues?.find(item => item.name.includes(field.name));
				if (matchingItem) {
					return matchingItem;
				}
				return {
					name: field.name,
					value: null,
					rowValue: null
				};
			});
		}

		const mobileControls = convertFieldsForMobile(
			this.columns as any,
			fieldValues,
			this.__columns,
			this.name,
			false
		);
		Mobile.MobileView.setDataGridControlsProperties(mobileControls, this.name);
	});

	this.subscribe('trigger:OnDynamicSelectSearch', async (payload: string) => {
		try {
			const searchValue = JSON.parse(payload);
			const validRow = validRowValue(this.columns, this.__editingColumnsValues);
			await dynamicSearch(this.__columns, searchValue.name, searchValue.value, validRow);
			const mobileControls = convertFieldsForMobile(
				this.columns as any,
				this.__editingColumnsValues,
				this.__columns,
				this.name,
				true
			);
			Mobile.MobileView.setDataGridControlsProperties(mobileControls, this.name);
		} catch (error) {
			console.error(error);
		}
	});

	this.subscribe('trigger:OnDataGridEditingRowChange', async (value?: string) => {
		if (value) {
			const data = JSON.parse(value);
			const selectedColumn = this.__columns.find(column => column.name == data.name);
			const selectedValue =
				selectedColumn?.editorOptions?.options?.find(item => item.key == data.value) ||
				data.value;
			const currentRowData =
				this.__editingColumnsValues?.reduce?.(
					(previousValue, currentValue) => ({
						...previousValue,
						[currentValue.name]: currentValue.value
					}),
					{}
				) || {};

			const args = await this.setCellValueEvent({
				columnName: data.name,
				currentRowData,
				newData: { [data.name]: selectedValue },
				value: selectedValue
			});

			Object.entries<any>(args.newData || {}).forEach(([columnName, columnData]) => {
				const editType = this.columns.find(column => column.name === columnName)?.editType;

				if (editType) {
					let columnValue = columnData;

					if (
						editType !== 'multiLanguageTextBox' &&
						typeof columnData === 'object' &&
						!Array.isArray(columnData) &&
						columnData
					) {
						columnValue = columnData.value?.toString();
					}

					const _data = {
						name: columnName,
						value: columnValue,
						extraProps: data.extraProps
					};

					this.__editingColumnsValues = handleDataForMobile(
						this.__editingColumnsValues,
						_data,
						editType,
						this.columns,
						this.__columns
					);
				}
			});

			const mobileControls = convertFieldsForMobile(
				this.columns as any,
				this.__editingColumnsValues,
				this.__columns,
				this.name,
				primaryKeyControl
			);

			Mobile.MobileView.setDataGridControlsProperties(mobileControls, this.name);
		}
	});

	this.subscribe('trigger:OnDataGridCommitChanges', (value: string) => {
		const operation: IDataGridCommitEvent = JSON.parse(value);
		let isValid = true;
		primaryKeyControl = false;

		if (operation.order !== Order.Delete) {
			const validRow = validRowValue(this.columns, this.__editingColumnsValues);

			let isNullOrConflict = false;
			if (operation.order === Order.Add) {
				isNullOrConflict = this.__methods.getNullOrConflictControl(validRow, this.rows);
			}

			const isValidControl = !this.__methods.getValidationControl(
				validRow,
				null,
				this.columns
			);
			isValid = isValidControl && !isNullOrConflict;
		}

		switch (operation.order) {
			case Order.Add: {
				if (isValid) {
					extremeGridCommitEventAdd.call(this);
				}
				break;
			}
			case Order.Edit: {
				if (isValid) {
					extremeGridCommitEventEdit.call(this, operation);
				}
				break;
			}
			case Order.Delete: {
				extremeGridCommitEventDelete.call(this, operation);
				break;
			}
		}

		Mobile.MobileView.toggleLoading(false);
	});

	this.subscribe('trigger:OnPageSizeChange', (value: string) => {
		onPageSizeChange.call(this, JSON.parse(value));
	});

	this.subscribe('trigger:OnPageIndexChange', (value: string) => {
		const index = JSON.parse(value) - 1;
		onPageIndexChange.call(this, index);
	});

	this.subscribe('trigger:OnSortingChange', (value: string) => {
		onSortingChange.call(this, JSON.parse(value));
	});

	this.subscribe('trigger:OnSearchingChange', (value: string) => {
		if (this.dataSource?.name && !this.dataSource?.runAtServer) {
			onSearchingChange.call(this, value);
		} else {
			this.__searhcValue = value;
			onSearchingChangeStatic.call(this);
		}
	});

	this.subscribe('trigger:OnRefreshGridSettings', () => {
		if (this.dataSource?.name && !this.dataSource?.runAtServer) {
			this.__searhcValue = '';
		} else {
			this.__searhcValue = '';
			onSearchingChangeStatic.call(this);
		}
		const pageSize =
			Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable ? 20 : 10;
		this.__sorting = [];
		this.__filtering = [];
		this.__proxyChanging.paging = { ...this.paging, pageSize, currentPage: 1 };
		const uniqueFieldName = this.getUniqueDataField();
		const selectedRows =
			this.rows
				?.filter(row => row.selected)
				?.map(selectedRow => selectedRow?.data?.[uniqueFieldName]) || [];
		const onDataLoad = (data: IDataLoadEventArgs) => {
			this.__proxyChanging.rows = this.rows.map(r => ({
				...r,
				selected: selectedRows.includes(r.data[uniqueFieldName])
			}));
		};
		if (this.hasSubscriber('event:OnDataLoad')) {
			this.off('event:OnDataLoad', onDataLoad);
		}
		this.subscribe('event:OnDataLoad', onDataLoad);
		this.reload(true);
	});

	this.subscribe('trigger:OnFilteringChange', (value: string) => {
		const _columns: IDataGridColumnCollection = JSON.parse(value);

		_columns.forEach(_column => {
			if ((_column as any).sortChanged) {
				const sort: any = _column.sorting;
				sort.direction = sort.order;
				delete sort.order;
				delete sort.enabled;
				sort['columnName'] = _column.name;
				this.__sorting = [sort];
			}
			if ((_column as any).filterChanged) {
				const filterItem: any = _column.filtering;
				delete filterItem.enabled;
				filterItem['values'] = [];
				filterItem['columnName'] = _column.name;

				if (_column.cellType === 'number' || _column.cellType === 'dateRange') {
					filterItem['value'] = parseInt(_column.filtering.value);

					if (_column.filtering.operation === ('contains' as any)) {
						filterItem['operation'] = 'equal';
					}
				}

				if (this.__filtering.find(x => x.columnName === filterItem.columnName)) {
					this.__filtering = this.__filtering?.map(filter => {
						if (filter.columnName === filterItem.columnName) {
							return filterItem;
						}
						return filter;
					});
				} else {
					this.__filtering.push(filterItem);
				}
			}

			return _column;
		});

		this.__proxyChanging.paging = { ...this.paging, currentPage: 1 };
		this.reload(true);
	});

	this.subscribe('trigger:OnExportToExcell', () => {
		Mobile.MobileView.exportData('', '', false);
		this.__methods.onExporting();
	});
}

export function onSearchingChangeStatic(this: DataGrid) {
	if (this.dataSource?.name && !this.dataSource?.runAtServer) {
		return;
	}

	this.__form.applyChanges().then(() => {
		const rows = this.rows.filter(row => {
			let filteringRow;
			const filteredRowCells = row.cells.filter(cell => cell.editType !== 'actionButton');
			const cellValues = filteredRowCells.map(filteredCell => {
				if (!filteredCell.value) {
					return '';
				}
				if (filteredCell.editType === 'multiLanguageTextBox') {
					return MLHelper.getMLText(filteredCell.value);
				}
				return filteredCell.value.toString();
			});
			const cellTexts = filteredRowCells.map(filteredCell => {
				if (!filteredCell.text) {
					return '';
				}
				return filteredCell.text.toString();
			});
			if (this.__searhcValue) {
				const result =
					cellValues.some(
						value =>
							value
								.toString()
								.toLocaleLowerCase()
								.indexOf(this.__searhcValue.toLocaleLowerCase()) >= 0
					) ||
					cellTexts.some(
						text =>
							text
								.toString()
								.toLocaleLowerCase()
								.indexOf(this.__searhcValue.toLocaleLowerCase()) >= 0
					);
				filteringRow = result && filteringStaticForMobile(this, row);
			} else {
				filteringRow = filteringStaticForMobile(this, row);
			}
			return filteringRow;
		});

		Mobile.MobileView.setFormDataGridRows({
			controlId: this.name,
			rows: handleRowsForMobile.call(this, rows),
			count: this.__totalCount,
			pageIndex: this.paging.currentPage
		});
	});
}

function extremeGridCommitEventAdd(this: DataGrid) {
	const changes: any = {};
	const uniqDataField: string = this.getUniqueDataField();
	const uniqDataValues = this.__editingColumnsValues.find(
		editingColumnValue => editingColumnValue.name === uniqDataField
	);
	const isUniqDataFieldAutoIncrement = this.columns.find(
		column => column.name === uniqDataField
	).autoIncrement;

	if (uniqDataValues?.value) {
		this.addValueByUniqData(uniqDataValues, changes, uniqDataField);
	} else if (isNullOrUndefined(uniqDataValues?.value) && !isUniqDataFieldAutoIncrement) {
		const message: any = {
			typeName: 'ShowMessageAction',
			dialogType: 'Toaster',
			alertType: 'Warning',
			id: createGuid(),
			messages: findMessage.get('101632').replace('{0}', uniqDataField),
			name: findMessage.get('100652'),
			enabled: true
		};
		Mobile.MobileView.mobileClientSideMessages([message]);
		Mobile.MobileView.gridRowCommitResponse({ status: false });
	} else if (isUniqDataFieldAutoIncrement) {
		this.addValueByAutoIncrement(changes);
	}
}

function extremeGridCommitEventEdit(this: DataGrid, operation: IDataGridCommitEvent) {
	let changes: any = {};
	const uniqDataField = this.getUniqueDataField();
	const primaryColumn = this.columns.find(item => item.isPrimaryKey);
	const uniqDataFieldValue = this.__editingColumnsValues.find(
		editingColumnValue => editingColumnValue.name === uniqDataField
	)?.rowValue;
	const rowsAsData = this.rows.map(row =>
		row.cells.reduce((result, current) => ({ ...result, [current.name]: current.value }), {})
	);
	const currentValue = rowsAsData?.find(row => row[uniqDataField] === uniqDataFieldValue);
	const currentValueIndex = this.rows.findIndex(
		row => row.cells.find(cell => cell.name === uniqDataField).value === uniqDataFieldValue
	);

	const updatedColumns = this.__editingColumnsValues.map(column => {
		const findColumn = this.__columns.find(item => item.name == column.name);
		if (findColumn?.editorType == 'SelectBox') {
			return {
				...column,
				rowValue: findColumn.editorOptions.options.find(
					option => option.value == column.rowValue
				)
			};
		}
		return column;
	});
	changes = updatedColumns.reduce(
		(result, current) => ({ ...result, [current.name]: current.rowValue }),
		changes
	);
	const rows = getRowsWithCells(this.rows);
	const unchangedRows = [...rows];
	const editedRowData = rows.find(row =>
		rowEditingPrimaryControl(row[uniqDataField], operation.key, primaryColumn)
	);
	const editingRowIndex = rows.findIndex(row =>
		rowEditingPrimaryControl(row[uniqDataField], operation.key, primaryColumn)
	);
	rows[editingRowIndex] = changes;

	if (!currentValue || currentValueIndex === editingRowIndex) {
		const onRowUpdatingArgs: IRowUpdatingEventArgs = {
			cancel: false,
			newRow: changes,
			oldRow: editedRowData,
			rows: unchangedRows
		};

		onRowUpdating.call(this, onRowUpdatingArgs).then(() => {
			const args: any = {};

			const customizer = (rowValue: any, oldRowValue: any) =>
				(isNullOrUndefined(rowValue) && isNullOrUndefined(oldRowValue)) ||
				(isNaN(rowValue) && isNaN(oldRowValue))
					? true
					: undefined;

			args.newRow = rows.find(row => {
				const oldRow = rowsAsData.find(oldR =>
					rowEditingPrimaryControl(oldR[uniqDataField], row[uniqDataField], primaryColumn)
				);
				const result = !isEqualWith(row, oldRow, customizer);

				if (result) {
					args.oldRow = oldRow;

					return true;
				}

				return false;
			});

			if (args.newRow) {
				onRowUpdated.call(this, args);

				this.onCommitChanges(
					{
						data: changes,
						key: args.oldRow[uniqDataField],
						type: 'update'
					},
					changes
				);

				return;
			}

			this.onCommitChanges(
				{
					data: changes,
					key: changes[uniqDataField],
					type: 'update'
				},
				changes
			);
		});

		return;
	}

	Mobile.MobileView.gridRowCommitResponse({ status: false });
	this.__form.showToaster(
		findMessage.get('100652'),
		findMessage.get('101633').replace('{0}', uniqDataFieldValue).replace('{1}', uniqDataField),
		'Warning'
	);
}

function extremeGridCommitEventDelete(this: DataGrid, operation: IDataGridCommitEvent) {
	const uniqDataField = this.getUniqueDataField();
	const rows = getRowsWithCells(this.rows);
	const primaryColumn = this.columns.find(item => item.isPrimaryKey);
	const deletedRow = rows.find(row =>
		rowEditingPrimaryControl(row[uniqDataField], operation.key, primaryColumn)
	);
	const onRowRemovingArgs: any = {
		cancel: false,
		row: deletedRow,
		rows: rows
	};

	onRowRemoving.call(this, onRowRemovingArgs).then(() => {
		onRowRemoved.call(this, { row: deletedRow });
		this.onCommitChanges({ key: deletedRow[uniqDataField], type: 'remove' } as any);
	});
}

function getRowsWithCells(rows: Array<any>, extendComboBox = false) {
	return rows.map(row =>
		row.cells.reduce((result, cell) => {
			if (extendComboBox && cell.editType === 'comboBox') {
				return {
					...result,
					[cell.name]: {
						text: cell.text,
						value: cell.value
					}
				};
			}

			return {
				...result,
				[cell.name]: cell.value
			};
		}, {})
	);
}

function removeDuplicateObjects(data, uniqueField) {
	const uniqueObjects = {};
	for (const obj of data) {
		const id = obj[uniqueField];
		if (!uniqueObjects[id]) {
			uniqueObjects[id] = obj;
		}
	}
	return Object.values(uniqueObjects);
}

function rowEditingPrimaryControl(
	row: any,
	mobileValue: any,
	primaryColumn: IControlDataGridColumn<IControlProperties>
): boolean {
	if (primaryColumn.editType.includes('date') || primaryColumn.editType == 'timePicker') {
		return dayjs(new Date(row)).isSame(dayjs(new Date(mobileValue)));
	}

	if (row && typeof row === 'object') {
		if (primaryColumn.editType === 'comboBox') {
			return (row.key?.toString() || '') === mobileValue.toString();
		} else if (primaryColumn.editType === 'multiLanguageTextBox') {
			if (typeof mobileValue === 'string') {
				return isEqual(row, JSON.parse(mobileValue));
			}
			return isEqual(row, mobileValue);
		}
	}

	if (mobileValue && typeof mobileValue === 'object') {
		if (primaryColumn.editType === 'comboBox') {
			return row.toString() === (mobileValue.key?.toString() || '');
		}
	}

	return row.toString() === mobileValue.toString();
}

function validRowValue(columns, editingColumnValues) {
	const validRow = {};
	columns.forEach(column => {
		const { name } = column;
		const editingColumn = editingColumnValues.find(e => e.name === name);
		validRow[name] = editingColumn?.value || null;
	});
	return validRow;
}

async function dynamicSearch(
	columns: Array<BCExtremeGridEntities.IExtremeGridColumn>,
	name: string,
	value: string,
	row: IDictionary<any>
) {
	try {
		const columnValue = columns.find(item => item.name == name);

		const item = await columnValue?.editorOptions.mobileSearchPromise(value, { data: row });

		columnValue.data = item.data;
		columnValue.editorOptions.options = item.items;
	} catch (error) {
		console.error(error);
	}
}
